import { NotificationForm } from '../NotificationForm/NotificationForm.component';
import { AnimatePresence } from 'framer-motion';
import React, { ComponentPropsWithRef, FC, useEffect, useState } from 'react';
import { deleteUserNotification, saveUserNotification } from '@api/business/UserApi.utils';
import { FadeInElement } from '@components/animations/FadeInElement/FadeInElement.component';
import { PrimaryButton } from '@components/buttons/FlatButton/FlatButton.component';
import { PlcMessage } from '@components/PlcMessage/PlcMessage.component';
import { Text } from '@components/Text/Text.component';
import { useWarnIfUnsavedChanges } from '@form/business/UnsavedChange.hook';
import { ConfirmationModal } from '@layout/components/Modal/ConfirmationModal.component';
import { useTranslation } from '@locales/useTranslation.hook';
import { City } from '@meteo/types';
import { PlcNotification } from '@notifs/types';
import { Breakpoint, useIsBreakpointDown } from '@styles/breakpoints';
import classnames from 'classnames';

import './NotificationList.scss';

export const NEW_NOTIF_ID = -1;
const MAX_NOTIF_LENGTH = 50;

interface Props extends ComponentPropsWithRef<'div'> {
  notifications: Array<Partial<PlcNotification>>;
  favoriteLocalities: City[];
  onNotificationCreated?: () => void;
}

const NotificationList: FC<Props> = ({
  className,
  favoriteLocalities,
  notifications: notificationsProps,
  onNotificationCreated,
  ...rest
}) => {
  const { t } = useTranslation(['notifications']);
  const [toDelete, setToDelete] = useState<Partial<PlcNotification>>();
  const [isCreationInProgress, setIsCreationInProgress] = useState(false);
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState(false);
  const [notifications, setNotifications] = useState(notificationsProps);
  const isMobile = useIsBreakpointDown(Breakpoint.s);
  const [error, setError] = useState<Partial<PlcNotification>>();
  const [errorMessage, setErrorMessage] = useState<string>();

  useEffect(() => {
    setNotifications(notificationsProps);
  }, [notificationsProps]);

  useWarnIfUnsavedChanges(isCreationInProgress);

  function addNewForm() {
    if (notifications.findIndex(n => n.id === NEW_NOTIF_ID) === -1) {
      setNotifications([{ id: NEW_NOTIF_ID }, ...notifications]);
      setIsCreationInProgress(true);
    }
  }

  if (favoriteLocalities.length === 0) {
    return null;
  }

  function closeDeleteConfirmationModal() {
    setIsDeleteConfirmationModalOpen(false);
  }

  function handleDeleteNotification(state: Partial<PlcNotification>) {
    setToDelete(state);
    setIsDeleteConfirmationModalOpen(true);
  }

  async function handleSaveNotification(notif: PlcNotification) {
    try {
      const response = await saveUserNotification(notif);
      const newNotifications = [...notifications];

      setError(undefined);
      setErrorMessage(undefined);
      const responseIndex = notifications.findIndex(n => n.id === response.data.id);
      if (responseIndex !== -1) {
        newNotifications[responseIndex] = notif;
        setNotifications(newNotifications);
      } else {
        setNotifications([{ ...notif, id: response.data.id }, ...notifications.filter(n => n.id !== NEW_NOTIF_ID)]);
        if (onNotificationCreated) {
          onNotificationCreated();
        }
      }
      setIsCreationInProgress(false);
    } catch (err) {
      console.error('Error while saving notification', err);
      setError(notif);
      setErrorMessage('notifications.error.save');
    }
  }

  async function onDeleteConfirm() {
    closeDeleteConfirmationModal();

    if (!toDelete || !toDelete.id) {
      return;
    }

    if (toDelete.id === NEW_NOTIF_ID) {
      setIsCreationInProgress(false);
      setToDelete(undefined);
      setNotifications(notifications.filter(n => n.id !== toDelete.id));
      return;
    }

    if (toDelete.notificationType) {
      const notif = notifications.find(n => n.id === toDelete.id && n.notificationType === toDelete.notificationType);

      if (!notif) {
        return;
      }

      const result = await deleteUserNotification(notif as PlcNotification);
      if (result) {
        setError(undefined);
        setErrorMessage(undefined);
        setToDelete(undefined);
        setNotifications(
          notifications.filter(n => !(n.id === toDelete.id && n.notificationType === toDelete.notificationType))
        );
      } else {
        setErrorMessage('common.error.generic');
        setError(toDelete);
      }
    }
  }

  const sortedNotifications = notifications.sort((a, b) =>
    (a?.locality?.properties.name || '').localeCompare(b?.locality?.properties.name || '')
  );
  const rootClass = classnames('notification-list', className);

  return (
    <div className={rootClass} {...rest}>
      <div className="notification-list-title">
        <Text i18nKey="notifications.title" i18nOptions={{ count: notifications.length }} tag="h2" variant="h4" />
        <PrimaryButton
          disabled={isCreationInProgress || notifications.length >= MAX_NOTIF_LENGTH}
          isSmall={!isMobile}
          onClick={addNewForm}
          textProps={{ i18nKey: 'notifications.add-notification' }}
        />
      </div>
      {errorMessage && <PlcMessage className="plc-mb-m" i18nKey={errorMessage} />}
      <ul>
        <AnimatePresence>
          {sortedNotifications.map((n, i) => {
            const key = n.id === NEW_NOTIF_ID ? `new-${i}` : `${n.notificationType}-${n.id}`;
            return (
              <FadeInElement key={key} show={true}>
                <NotificationForm
                  favoriteLocalities={favoriteLocalities}
                  initialState={n}
                  isError={error && error.id === n.id && error.notificationType === n.notificationType}
                  onDelete={handleDeleteNotification}
                  onSave={handleSaveNotification}
                />
              </FadeInElement>
            );
          })}
        </AnimatePresence>
      </ul>
      <ConfirmationModal
        onConfirm={onDeleteConfirm}
        onDecline={closeDeleteConfirmationModal}
        title=""
        visible={isDeleteConfirmationModalOpen}
      >
        {t('notifications.delete.confirmation.label')}
      </ConfirmationModal>
    </div>
  );
};

export { NotificationList };
